import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  useSEO,
  siteUrl,
  breadcrumbJsonLd,
  serviceJsonLd,
  faqPageJsonLd,
} from "@/lib/seo";
import { useLang } from "@/lib/i18n";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";

/**
 * SeoLandingPage — reusable template voor SEO-landingspagina's.
 *
 * Props:
 *   data: {
 *     slug, h1, lead,
 *     metaTitle, metaDesc, canonicalPath,
 *     serviceName, serviceDescription,
 *     breadcrumbName,
 *     intro: [paragraphs],
 *     sections: [{ heading, paragraphs: [], bullets?: [] }],
 *     usp: [{ title, body }],
 *     faqs: [{ id, q, a }],
 *     servicesLinks: [{ to, label }],   // diensten interne links
 *     locationsLinks: [{ to, label }],  // locatie interne links
 *     ctas: { primary: {to, label}, secondary: {to, label} },
 *   }
 *   lang: 'nl' | 'en'
 */
const SeoLandingPage = ({ data, lang = "nl" }) => {
  const canonical = siteUrl(data.canonicalPath);
  const breadcrumbs = [
    { name: lang === "nl" ? "Home" : "Home", url: siteUrl("/") },
    { name: data.breadcrumbName, url: canonical },
  ];

  useSEO({
    title: data.metaTitle,
    description: data.metaDesc,
    canonical,
    image: siteUrl("/og-image-ai.jpg?v=4"),
    lang,
    jsonLd: [
      {
        id: `seo-${data.slug}-service`,
        data: serviceJsonLd({
          name: data.serviceName,
          description: data.serviceDescription,
          url: canonical,
        }),
      },
      {
        id: `seo-${data.slug}-breadcrumb`,
        data: breadcrumbJsonLd(breadcrumbs),
      },
      {
        id: `seo-${data.slug}-faq`,
        data: faqPageJsonLd(
          data.faqs.map((f, i) => ({ id: f.id || `faq-${i}`, q_nl: f.q, a_nl: f.a })),
          "nl",
        ),
      },
    ],
  });

  return (
    <main data-testid={`seo-${data.slug}-page`} className="min-h-screen">
      {/* HERO */}
      <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 cyber-grid opacity-15" />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/6 blur-3xl -z-10" />
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <nav className="text-xs text-white/45 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#00D4FF]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white/70">{data.breadcrumbName}</span>
          </nav>
          <p className="section-overline mb-4">{data.overline || "SEO landing"}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            {data.h1}
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-white/65 leading-relaxed max-w-3xl">
            {data.lead}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={data.ctas.primary.to} data-testid={`seo-${data.slug}-cta-primary`}>
              <button className="btn-primary">
                {data.ctas.primary.label} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to={data.ctas.secondary.to} data-testid={`seo-${data.slug}-cta-secondary`}>
              <button className="btn-secondary">{data.ctas.secondary.label}</button>
            </Link>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          {/* Intro paragraphs */}
          <div className="prose-block text-white/75 text-base sm:text-[17px] leading-[1.75] space-y-5">
            {data.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* USP grid */}
          {data.usp && data.usp.length > 0 && (
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
              {data.usp.map((u, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 bg-[#0F0F23] p-6"
                >
                  <h3 className="text-white text-base font-semibold mb-2 tracking-tight">
                    {u.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{u.body}</p>
                </div>
              ))}
            </div>
          )}

          {/* Sections with paragraphs + optional bullets */}
          {data.sections.map((s, i) => (
            <section key={i} className="mt-16 sm:mt-20">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white leading-[1.15] mb-5">
                {s.heading}
              </h2>
              <div className="text-white/70 text-base leading-[1.75] space-y-4">
                {s.paragraphs.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-6 space-y-2.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-3 text-white/70 text-[15px]">
                      <CheckCircle2 className="w-4 h-4 text-[#00D4FF] mt-1 shrink-0" strokeWidth={2} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Internal services links */}
          {data.servicesLinks && data.servicesLinks.length > 0 && (
            <div
              data-testid={`seo-${data.slug}-services-links`}
              className="mt-16 pt-10 border-t border-white/5"
            >
              <h3 className="text-xs uppercase tracking-[0.16em] text-white/45 font-semibold mb-4">
                {lang === "nl" ? "Verken onze diensten" : "Explore our services"}
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.servicesLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0F0F23] px-4 py-2 text-sm text-white/75 hover:text-white hover:border-[#00D4FF]/40 transition-colors"
                  >
                    {l.label} <ArrowRight className="w-3 h-3 text-[#00D4FF]" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Cluster (gerelateerde SEO landingspagina's) */}
          {data.clusterLinks && data.clusterLinks.length > 0 && (
            <div
              data-testid={`seo-${data.slug}-cluster-links`}
              className="mt-10"
            >
              <h3 className="text-xs uppercase tracking-[0.16em] text-white/45 font-semibold mb-4">
                {lang === "nl" ? "Gerelateerde onderwerpen" : "Related topics"}
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.clusterLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0F0F23] px-4 py-2 text-sm text-white/75 hover:text-white hover:border-[#00D4FF]/40 transition-colors"
                  >
                    {l.label} <ArrowRight className="w-3 h-3 text-[#00D4FF]" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Kennisbank links */}
          {data.kennisbankLinks && data.kennisbankLinks.length > 0 && (
            <div
              data-testid={`seo-${data.slug}-kennisbank-links`}
              className="mt-10"
            >
              <h3 className="text-xs uppercase tracking-[0.16em] text-white/45 font-semibold mb-4">
                {lang === "nl" ? "Lees verder in de kennisbank" : "Read more in the knowledge base"}
              </h3>
              <ul className="space-y-2.5">
                {data.kennisbankLinks.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="inline-flex items-center gap-2 text-[15px] text-white/75 hover:text-[#00D4FF] transition-colors"
                    >
                      <ArrowRight className="w-3.5 h-3.5 text-[#00D4FF]" />
                      <span>{l.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Internal locations links */}
          {data.locationsLinks && data.locationsLinks.length > 0 && (
            <div
              data-testid={`seo-${data.slug}-locations-links`}
              className="mt-10"
            >
              <h3 className="text-xs uppercase tracking-[0.16em] text-white/45 font-semibold mb-4">
                {lang === "nl" ? "Actief in heel Nederland" : "Active across the Netherlands"}
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.locationsLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0F0F23] px-3 py-1.5 text-[13px] text-white/70 hover:text-white hover:border-[#00D4FF]/40 transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          {data.faqs && data.faqs.length > 0 && (
            <section className="mt-20">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-8">
                {lang === "nl" ? "Veelgestelde vragen" : "Frequently asked questions"}
              </h2>
              <FAQAccordion
                items={data.faqs.map((f, i) => ({
                  id: f.id || `faq-${i}`,
                  q_nl: f.q,
                  a_nl: f.a,
                  q_en: f.q,
                  a_en: f.a,
                }))}
                dataTestid={`seo-${data.slug}-faq`}
              />
            </section>
          )}

          {/* Inline CTA */}
          <div
            data-testid={`seo-${data.slug}-bottom-cta`}
            className="mt-20 rounded-2xl border border-white/10 bg-[#0F0F23] p-7 sm:p-10"
          >
            <p className="section-overline mb-3">
              {lang === "nl" ? "Volgende stap" : "Next step"}
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight mb-4">
              {data.ctaBlock?.title || (lang === "nl" ? "Klaar om kennis te maken?" : "Ready to get in touch?")}
            </h2>
            <p className="text-white/65 leading-relaxed mb-7 max-w-2xl">
              {data.ctaBlock?.body ||
                (lang === "nl"
                  ? "Wij komen graag langs voor een rondleiding en een vrijblijvend voorstel op uw eigen voertuigen."
                  : "We are happy to visit for a walkthrough and a no-obligation proposal on your own vehicles.")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={data.ctas.primary.to}>
                <button className="btn-primary">
                  {data.ctas.primary.label} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to={data.ctas.secondary.to}>
                <button className="btn-secondary">{data.ctas.secondary.label}</button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default SeoLandingPage;
